import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Edit, Trash2, Save, X, Plus, StickyNote, Upload, Image as ImageIcon, User, Mail, Phone, MapPin, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { compressImage } from '../utils/imageCompression';
import BargeLogForm from '../components/BargeLogForm';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const ServiceDetail = ({ user }) => {
  const { serviceId } = useParams();
  const id = serviceId; // Use serviceId from route params
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Notes
  const [notes, setNotes] = useState([]);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  
  // Photos
  const [photos, setPhotos] = useState([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [photoDescription, setPhotoDescription] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  
  // Barge Logs
  const [bargeLogs, setBargeLogs] = useState([]);
  const [isBargeLogDialogOpen, setIsBargeLogDialogOpen] = useState(false);
  const [editingBargeLog, setEditingBargeLog] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchServiceDetail();
        await fetchClients();
        await fetchEmployees();
        await fetchNotes();
        await fetchPhotos();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Fetch barge logs when service type is "Barging"
  useEffect(() => {
    if (service && service.service_type === 'Barging') {
      fetchBargeLogs();
    }
  }, [service]);

  const fetchServiceDetail = async () => {
    try {
      const response = await axios.get(`${API}/services/${id}`);
      setService(response.data);
      setEditData({
        client_id: response.data.client_id,
        date: response.data.date,
        service_status: response.data.service_status,
        notes: response.data.notes || ''
      });
      
      // Fetch client details if client_id exists
      if (response.data.client_id) {
        try {
          const clientResponse = await axios.get(`${API}/users/${response.data.client_id}`);
          setClientInfo(clientResponse.data);
        } catch (clientError) {
          console.warn('Client not found, but service loaded:', clientError);
          // Client doesn't exist, but we can still show the service
          setClientInfo(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch service details:', error);
      toast.error('Failed to fetch service details');
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setClients(response.data.filter(u => u.role === 'client'));
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setEmployees(response.data.filter(u => u.role === 'employee' || u.role === 'sub_contractor'));
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const handleMarkComplete = async () => {
    try {
      await axios.post(`${API}/services/${id}/complete`);
      toast.success('Service marked as complete!');
      fetchServiceDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark service as complete');
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/services/${id}/notes`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes');
    }
  };

  const fetchPhotos = async () => {
    try {
      console.log('[ServiceDetail] Fetching photos for service:', id);
      const response = await axios.get(`${API}/services/${id}/photos`);
      console.log('[ServiceDetail] Photos response:', {
        count: response.data?.length,
        isArray: Array.isArray(response.data),
        photos: response.data?.map(p => ({
          id: p.id,
          file_path: p.file_path,
          description: p.description
        }))
      });
      setPhotos(response.data);
      console.log('[ServiceDetail] Photos state updated with', response.data?.length, 'photos');
    } catch (error) {
      console.error('[ServiceDetail] Failed to fetch photos:', error);
    }
  };

  const fetchBargeLogs = async () => {
    try {
      const response = await axios.get(`${API}/barge-logs/${id}`);
      setBargeLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch barge logs:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/services/${id}`, editData);
      toast.success('Service updated!');
      setIsEditing(false);
      fetchServiceDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this service entry?')) return;

    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Service deleted!');
      navigate('/services');
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  // Note handlers
  const handleSaveNote = async () => {
    if (!noteContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    try {
      if (editingNote) {
        await axios.put(`${API}/services/${id}/notes/${editingNote.id}`, { content: noteContent });
        toast.success('Note updated!');
      } else {
        await axios.post(`${API}/services/${id}/notes`, { service_id: id, content: noteContent });
        toast.success('Note added!');
      }
      setIsNoteDialogOpen(false);
      setNoteContent('');
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await axios.delete(`${API}/services/${id}/notes/${noteId}`);
      toast.success('Note deleted!');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  // Photo handlers
  const handlePhotoUpload = async () => {
    if (!photoFile) {
      toast.error('Please select a photo');
      return;
    }

    // Validate file type
    if (!photoFile.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 10MB)
    if (photoFile.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    try {
      // Compress image before upload
      toast.info('Compressing image...');
      const compressedFile = await compressImage(photoFile, 1280, 1280, 0.8);
      
      if (!compressedFile || compressedFile.size === 0) {
        throw new Error('Image compression failed');
      }

      toast.info('Uploading photo...');
      const formData = new FormData();
      formData.append('file', compressedFile);
      if (photoDescription) {
        formData.append('description', photoDescription);
      }

      await axios.post(`${API}/services/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });

      toast.success('Photo uploaded!');
      setIsUploadOpen(false);
      setPhotoFile(null);
      setPhotoDescription('');
      fetchPhotos();
    } catch (error) {
      console.error('Upload error:', error);
      if (error.message.includes('compression')) {
        toast.error('Failed to compress image. Try a different photo.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || error.message || 'Failed to upload photo');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!service) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <Button
          onClick={() => navigate('/services')}
          className="mb-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold min-h-[48px] shadow-md"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Services
        </Button>

        {/* Service Header */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{service.service_type}</h1>
              <p className="text-lg text-gray-700 font-semibold">Client: {service.client_name}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <span className={`px-4 py-2 rounded-lg text-sm font-bold border ${getStatusColor(service.service_status)}`}>
                {service.service_status}
              </span>
            </div>
          </div>

          {/* Service Info */}
          {!isEditing ? (
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-gray-600">Date</p>
                <p className="text-base text-gray-900">{new Date(service.date).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>
              {service.notes && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Notes</p>
                  <p className="text-base text-gray-900 whitespace-pre-wrap">{service.notes}</p>
                </div>
              )}
              <div>
                <p className="text-sm font-semibold text-gray-600">Created By</p>
                <p className="text-base text-gray-900">{service.created_by_name} on {new Date(service.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}</p>
              </div>

              {/* Assigned Employees */}
              {service.assigned_employees && service.assigned_employees.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Assigned Employees</p>
                  <div className="flex flex-wrap gap-2">
                    {service.assigned_employees.map(empId => {
                      const emp = employees.find(e => e.id === empId);
                      return emp ? (
                        <span key={empId} className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {emp.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Completion Info */}
              {service.completed_by_name && (
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-sm font-semibold text-green-800">Completed By</p>
                  <p className="text-base text-green-900">
                    {service.completed_by_name} on {new Date(service.completed_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                  </p>
                </div>
              )}

              {/* Mark Complete Button - For assigned employees and admins */}
              {service.service_status !== 'Completed' && 
               ((user.role === 'admin' || user.role === 'supervisor') || service.assigned_employees?.includes(user.id)) && (
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    onClick={handleMarkComplete}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold min-h-[44px]"
                  >
                    ✓ Mark Job Complete
                  </Button>
                </div>
              )}

              {/* Admin Actions */}
              {(user.role === 'admin' || user.role === 'supervisor') && (
                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <Button
                    onClick={() => {
                      setIsEditing(true);
                      setEditData({
                        client_id: service.client_id,
                        date: service.date,
                        service_status: service.service_status,
                        notes: service.notes || '',
                        assigned_employees: service.assigned_employees || []
                      });
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold min-h-[44px]"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    onClick={handleDelete}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold min-h-[44px]"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="font-bold text-base mb-2 block">Client</Label>
                <Select value={editData.client_id} onValueChange={(value) => setEditData({...editData, client_id: value})}>
                  <SelectTrigger className="border-2 border-gray-400 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name} - {client.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-bold text-base mb-2 block">Date</Label>
                <Input
                  type="date"
                  value={editData.date}
                  onChange={(e) => setEditData({...editData, date: e.target.value})}
                  className="border-2 border-gray-400 h-12"
                />
              </div>

              <div>
                <Label className="font-bold text-base mb-2 block">Status</Label>
                <Select value={editData.service_status} onValueChange={(value) => setEditData({...editData, service_status: value})}>
                  <SelectTrigger className="border-2 border-gray-400 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assign Employees - For Barging services */}
              {service && service.service_type === 'Barging' && (
                <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <Label className="font-bold text-base mb-3 block text-blue-900">
                    👷 Assign Employees to Fill Barge Logs
                  </Label>
                  <p className="text-sm text-gray-600 mb-3">
                    Select employees who are authorized to fill out barge log details for this service
                  </p>
                  <div className="space-y-2">
                    {employees.map((employee) => (
                      <label
                        key={employee.id}
                        className="flex items-center gap-3 p-2 hover:bg-blue-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(editData.assigned_employees || []).includes(employee.id)}
                          onChange={(e) => {
                            const currentAssigned = editData.assigned_employees || [];
                            const newAssigned = e.target.checked
                              ? [...currentAssigned, employee.id]
                              : currentAssigned.filter(id => id !== employee.id);
                            setEditData({...editData, assigned_employees: newAssigned});
                          }}
                          className="w-5 h-5"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{employee.name}</p>
                          <p className="text-xs text-gray-600">{employee.email}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {editData.assigned_employees && editData.assigned_employees.length > 0 && (
                    <p className="text-sm text-blue-700 mt-3 font-semibold">
                      ✓ {editData.assigned_employees.length} employee(s) assigned
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="font-bold text-base mb-2 block">Assigned Employees</Label>
                <div className="space-y-2 border-2 border-gray-400 rounded p-3 max-h-48 overflow-y-auto">
                  {employees.map((employee) => (
                    <label key={employee.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editData.assigned_employees?.includes(employee.id) || false}
                        onChange={(e) => {
                          const currentAssigned = editData.assigned_employees || [];
                          if (e.target.checked) {
                            setEditData({...editData, assigned_employees: [...currentAssigned, employee.id]});
                          } else {
                            setEditData({...editData, assigned_employees: currentAssigned.filter(id => id !== employee.id)});
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{employee.name} ({employee.role})</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {editData.assigned_employees?.length || 0} employee(s) assigned
                </p>
              </div>

              <div>
                <Label className="font-bold text-base mb-2 block">Notes</Label>
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  placeholder="Add any notes or details..."
                  className="border-2 border-gray-400"
                  rows={4}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleUpdate}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold min-h-[44px]"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setEditData({
                      client_id: service.client_id,
                      date: service.date,
                      service_status: service.service_status,
                      notes: service.notes || ''
                    });
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold min-h-[44px]"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Client Information */}
        {clientInfo && (
          <div className="bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Client Information</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-600">Name:</span>
                <span className="text-base text-gray-900">{clientInfo.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-semibold text-gray-600">Email:</span>
                <span className="text-base text-gray-900">{clientInfo.email}</span>
              </div>
              {clientInfo.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-600">Phone:</span>
                  <span className="text-base text-gray-900">{clientInfo.phone}</span>
                </div>
              )}
              {clientInfo.company && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-semibold text-gray-600">Company:</span>
                  <span className="text-base text-gray-900">{clientInfo.company}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Service Notes */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <StickyNote className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Service Notes</h2>
              {notes.length > 0 && (
                <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-orange-500 rounded-full">
                  {notes.length}
                </span>
              )}
            </div>
            <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => {
                    setEditingNote(null);
                    setNoteContent('');
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold min-h-[44px]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">{editingNote ? 'Edit Note' : 'Add Note'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Textarea
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Enter your note..."
                    className="border-2 border-gray-400"
                    rows={6}
                  />
                  <Button onClick={handleSaveNote} className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-bold text-lg min-h-[52px]">
                    {editingNote ? 'Update Note' : 'Add Note'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {notes.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No notes yet. Add one to get started.</p>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note.id} className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{note.user_name}</p>
                      <p className="text-xs text-gray-600">{new Date(note.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}</p>
                    </div>
                    {((user.role === 'admin' || user.role === 'supervisor') || note.user_id === user.id) && (
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingNote(note);
                            setNoteContent(note.content);
                            setIsNoteDialogOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteNote(note.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Daily Barge Logs - Only for Barging service */}
        {service && service.service_type === 'Barging' && (
          <div className="bg-white border-2 border-blue-300 rounded-lg p-6 mb-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Daily Barge Logs</h2>
                {bargeLogs.length > 0 && (
                  <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-blue-500 rounded-full">
                    {bargeLogs.length}
                  </span>
                )}
              </div>
              <Dialog open={isBargeLogDialogOpen} onOpenChange={setIsBargeLogDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => setEditingBargeLog(null)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold min-h-[44px]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Barge Log
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold">
                      {editingBargeLog ? 'Edit Barge Log' : 'Add Daily Barge Log'}
                    </DialogTitle>
                  </DialogHeader>
                  <BargeLogForm
                    serviceId={id}
                    existingLog={editingBargeLog}
                    onSuccess={() => {
                      setIsBargeLogDialogOpen(false);
                      setEditingBargeLog(null);
                      fetchBargeLogs();
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            {bargeLogs.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No barge logs yet. Add one to track daily operations.</p>
            ) : (
              <div className="space-y-4">
                {bargeLogs.map((log) => (
                  <div key={log.id} className="bg-gradient-to-r from-blue-50 to-white border-2 border-blue-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-gray-900">
                          {new Date(log.date).toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {log.location} • Barge Type: {log.barge_type} • Client: {log.client}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Logged by {log.created_by_name} on {new Date(log.created_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
                        </p>
                      </div>
                      {(user.role === 'admin' || user.role === 'supervisor') && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setEditingBargeLog(log);
                              setIsBargeLogDialogOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={async () => {
                              if (window.confirm('Delete this barge log?')) {
                                try {
                                  await axios.delete(`${API}/barge-logs/${log.id}`);
                                  toast.success('Barge log deleted');
                                  fetchBargeLogs();
                                } catch (error) {
                                  toast.error('Failed to delete barge log');
                                }
                              }
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Time</p>
                        <p className="text-gray-900">{log.start_time} - {log.finish_time}</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Operating Time</p>
                        <p className="text-gray-900">{log.operating_time || 'N/A'} hrs</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Standby Time</p>
                        <p className="text-gray-900">{log.standby_time || 'N/A'} hrs</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Platform Time</p>
                        <p className="text-gray-900">{log.platform_time || 'N/A'} hrs</p>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      {log.equipment_skidsteer_hrs && (
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <p className="text-gray-600 font-semibold">Skidsteer</p>
                          <p className="text-gray-900">{log.equipment_skidsteer_hrs} hrs</p>
                        </div>
                      )}
                      {log.equipment_excavator_hrs && (
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <p className="text-gray-600 font-semibold">Excavator</p>
                          <p className="text-gray-900">{log.equipment_excavator_hrs} hrs</p>
                        </div>
                      )}
                      {log.equipment_other_hrs && (
                        <div className="bg-green-50 rounded p-2 border border-green-200">
                          <p className="text-gray-600 font-semibold">{log.equipment_other_name || 'Other'}</p>
                          <p className="text-gray-900">{log.equipment_other_hrs} hrs</p>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Load Description</p>
                        <p className="text-gray-900">{log.load_description}</p>
                      </div>
                      <div className="bg-white rounded p-2 border border-gray-200">
                        <p className="text-gray-600 font-semibold">Persons on Barge</p>
                        <p className="text-gray-900">{log.persons_on_barge}</p>
                      </div>
                    </div>

                    {(log.fuel_added || log.ramp_used) && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {log.fuel_added && (
                          <div className="bg-white rounded p-2 border border-gray-200">
                            <p className="text-gray-600 font-semibold">Fuel Added</p>
                            <p className="text-gray-900">{log.fuel_added}</p>
                          </div>
                        )}
                        {log.ramp_used && (
                          <div className="bg-white rounded p-2 border border-gray-200">
                            <p className="text-gray-600 font-semibold">Ramp Used</p>
                            <p className="text-gray-900">{log.ramp_used}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {log.comments && (
                      <div className="mt-3 bg-yellow-50 rounded p-3 border border-yellow-200">
                        <p className="text-gray-600 font-semibold text-sm mb-1">Comments</p>
                        <p className="text-gray-900 whitespace-pre-wrap">{log.comments}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Service Photos */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6 shadow-md">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <ImageIcon className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Service Photos</h2>
              {photos.length > 0 && (
                <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-orange-500 rounded-full">
                  {photos.length}
                </span>
              )}
            </div>
            <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold min-h-[44px]">
                  <Upload className="w-4 h-4 mr-2 upload-icon-idle" />
                  Upload Photo
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-gray-900 border-2 border-gray-400 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold">Upload Photo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="font-bold text-base mb-2 block">Select Photo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setPhotoFile(e.target.files[0])}
                      className="border-2 border-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="font-bold text-base mb-2 block">Description (Optional)</Label>
                    <Textarea
                      value={photoDescription}
                      onChange={(e) => setPhotoDescription(e.target.value)}
                      placeholder="Add a description..."
                      className="border-2 border-gray-400"
                      rows={3}
                    />
                  </div>
                  <Button onClick={handlePhotoUpload} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg min-h-[52px]">
                    Upload
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {photos.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No photos yet. Upload one to get started.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="cursor-pointer rounded-lg overflow-hidden border-2 border-gray-300 hover:border-blue-600 transition-all hover:scale-105"
                  onClick={() => setViewerPhoto(photo)}
                >
                  <div className="relative w-full pb-[100%] bg-gray-200">
                    <img
                      src={`${BACKEND_URL}${photo.file_path}`}
                      alt={photo.description || 'Service photo'}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                      onLoad={() => {
                        console.log('[ServiceDetail] Image loaded successfully:', photo.file_path);
                      }}
                      onError={(e) => {
                        console.error('[ServiceDetail] Image failed to load:', {
                          file_path: photo.file_path,
                          full_url: `${BACKEND_URL}${photo.file_path}`,
                          photo_id: photo.id,
                          timestamp: new Date().toISOString()
                        });
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect width="200" height="200" fill="%23CCCCCC"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23666666" font-size="14"%3EImage Error%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>
                  {photo.description && (
                    <p className="text-xs text-gray-600 px-2 py-1 truncate" title={photo.description}>{photo.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photo Viewer */}
        {viewerPhoto && (
          <div className="fixed inset-0 bg-black/95 z-50 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div>
                <p className="text-white font-semibold">{viewerPhoto.uploaded_by_name}</p>
                <p className="text-gray-400 text-sm">{new Date(viewerPhoto.timestamp).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => setViewerPhoto(null)}
                className="text-white hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 flex items-center justify-center p-4">
              <img
                src={`${BACKEND_URL}${viewerPhoto.file_path}`}
                alt={viewerPhoto.description || 'Photo'}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23374151"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="16"%3EImage not available%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
            {viewerPhoto.description && (
              <div className="p-4 border-t border-gray-700">
                <p className="text-white">{viewerPhoto.description}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceDetail;
